import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLoading, useAuth } from '../check_login';
import creativeTypes from '../../creative_types/types';
import { IoArrowBack } from "react-icons/io5";
import { motion } from 'framer-motion';
import PlaceholderDropdown from '../../common/PlaceholderDropdown';
import TagsInput from '../../common/TagsInput';
import '../../../assets/css/addForm.css';
import '../../../assets/css/creativePreview.css';

// SMS Character Counter Function
const calculateSMSStats = (text) => {
  if (!text) return { totalCount: 0, totalSMS: 0, gsm: 0, unicode: 0, nextPart: 160 };
  
  const textLength = text.length;
  const gsmChars = /^[A-Za-z0-9@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\[\]\|~€]*$/;
  const isUnicode = !gsmChars.test(text);
  
  let smsLimit = isUnicode ? 70 : 160;
  let totalSMS = Math.ceil(textLength / smsLimit);
  let nextPart = smsLimit - (textLength % smsLimit);
  
  if (nextPart === smsLimit) nextPart = 0;
  
  return {
    totalCount: textLength,
    totalSMS: totalSMS,
    gsm: isUnicode ? 0 : textLength,
    unicode: isUnicode ? textLength : 0,
    nextPart: nextPart
  };
};

const AddSMS = ({ pageTitle }) => {
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();
  const { user: currentUser } = useAuth();

  // Permission check
  const permissions = currentUser?.role?.permissions || {};
  const canAITranslateSMS = permissions.canAITranslateSMS || currentUser?.isAdmin;

  const [creativeData, setCreativeData] = useState({
    title: '',
    tags: [],
    body: '',
    creativeType: '',
  });

  const [languages, setLanguages] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [translationProgress, setTranslationProgress] = useState(0);
  const [currentTranslatingLanguage, setCurrentTranslatingLanguage] = useState('');
  const [totalLanguages, setTotalLanguages] = useState(0);
  const [creationMode, setCreationMode] = useState(canAITranslateSMS ? 'translate' : 'manual'); // 'translate' or 'manual'
  const [manualLanguageData, setManualLanguageData] = useState({}); // Store manual input for each language
  const [previewLanguage, setPreviewLanguage] = useState(null); // Selected language for preview
  const [emptyLanguages, setEmptyLanguages] = useState([]); // Track languages with empty content
  
  // Status states
  const [statuses, setStatuses] = useState([]);
  const [statusSystemEnabled, setStatusSystemEnabled] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await axios.get('/brand/languages/active');
        setLanguages(response.data);
      } catch (err) {
        console.error('Error fetching languages:', err);
      }
    };
    fetchLanguages();
  }, []);

  useEffect(() => {
    const fetchStatusData = async () => {
      try {
        const token = localStorage.getItem('token');
        const [statusesRes, settingsRes] = await Promise.all([
          axios.get('/api/statuses/list', { headers: { Authorization: `Bearer ${token}` }}),
          axios.get('/api/statuses/settings', { headers: { Authorization: `Bearer ${token}` }})
        ]);
        setStatuses(statusesRes.data);
        setStatusSystemEnabled(settingsRes.data.isEnabled);

        const createdStatus = statusesRes.data.find((s) => s.name === 'Created');
        if (createdStatus) {
          setSelectedStatus(createdStatus._id);
        }
      } catch (err) {
        console.error('Error fetching status data:', err);
      }
    };
    fetchStatusData();
  }, []);

  const handleChange = (e) => {
    setCreativeData({ ...creativeData, [e.target.name]: e.target.value });
  };

  const toggleLanguageSelection = (languageId) => {
    if (selectAll) {
      setSelectAll(false);
      setSelectedLanguages(languages.map(l => l._id).filter(id => id !== languageId));
    } else if (selectedLanguages.includes(languageId)) {
      setSelectedLanguages(selectedLanguages.filter((id) => id !== languageId));
    } else {
      setSelectedLanguages([...selectedLanguages, languageId]);
    }
  };

  const toggleSelectAll = () => {
    setSelectAll(!selectAll);
    setSelectedLanguages(!selectAll ? languages.map((lang) => lang._id) : []);
  };

  const handleManualLanguageChange = (languageId, field, value) => {
    setManualLanguageData(prev => ({
      ...prev,
      [languageId]: {
        ...prev[languageId],
        [field]: value
      }
    }));
    
    // Remove from empty languages if content is added
    if (value && value.trim() && field === 'body') {
      setEmptyLanguages(prev => prev.filter(id => id !== languageId));
    }
  };

  const removeLanguage = (languageId) => {
    setSelectedLanguages(prev => prev.filter(id => id !== languageId));
    setManualLanguageData(prev => {
      const updated = { ...prev };
      delete updated[languageId];
      return updated;
    });
    setEmptyLanguages(prev => prev.filter(id => id !== languageId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTranslationProgress(0);
    setCurrentTranslatingLanguage('');
    showLoading();

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You are not logged in');
        hideLoading();
        setIsSubmitting(false);
        return;
      }

      const selectedLanguagesForApi = selectAll
        ? languages.map((lang) => lang._id)
        : selectedLanguages;

      if (selectedLanguagesForApi.length === 0) {
        setError('Please select at least one language.');
        hideLoading();
        setIsSubmitting(false);
        return;
      }

      // Validate status if system is enabled
      if (statusSystemEnabled && !selectedStatus) {
        setError('Please select a status.');
        hideLoading();
        setIsSubmitting(false);
        return;
      }

      if (creationMode === 'manual') {
        // Validate manual input for all selected languages
        const emptyLangs = [];
        for (const langId of selectedLanguagesForApi) {
          const langData = manualLanguageData[langId];
          if (!langData || !langData.body || !langData.body.trim()) {
            emptyLangs.push(langId);
          }
        }
        
        if (emptyLangs.length > 0) {
          setEmptyLanguages(emptyLangs);
          const emptyLangNames = emptyLangs.map(id => {
            const lang = languages.find(l => l._id === id);
            return lang?.code?.toUpperCase() || lang?.languageName;
          }).join(', ');
          setError(`Please fill in content for: ${emptyLangNames}`);
          hideLoading();
          setIsSubmitting(false);
          return;
        }

        // Submit manual creative data
        const manualCreatives = selectedLanguagesForApi.map(langId => {
          const langData = manualLanguageData[langId];
          const language = languages.find(l => l._id === langId);
          return {
            title: creativeData.title,
            tags: creativeData.tags,
            creativeType: creativeData.creativeType,
            ...langData,
            language: langId,
            languageName: language?.languageName || 'Unknown',
            ...(statusSystemEnabled && selectedStatus ? { status: selectedStatus } : {})
          };
        });

        const response = await axios.post('/creative/manual/sms', {
          creatives: manualCreatives
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.success) {
          navigate('/library/creatives');
        }
        
      } else {
        // Original translate mode logic
        setTotalLanguages(selectedLanguagesForApi.length);

        // Get language names for progress tracking
        const selectedLanguageNames = selectedLanguagesForApi.map(langId => {
          const lang = languages.find(l => l._id === langId);
          return lang ? (lang.code?.toUpperCase() || lang.languageName) : 'Unknown';
        });

        const requestData = {
          ...creativeData,
          languages: selectedLanguagesForApi,
          ...(statusSystemEnabled && selectedStatus ? { status: selectedStatus } : {})
        };

        // Progress simulation
        const progressInterval = setInterval(() => {
          setTranslationProgress(prev => {
            if (prev < 90) {
              const increment = Math.random() * 15 + 5; // Random increment between 5-20%
              const newProgress = Math.min(prev + increment, 90);
              
              // Update current language being translated
              const currentLangIndex = Math.floor((newProgress / 90) * selectedLanguageNames.length);
              if (currentLangIndex < selectedLanguageNames.length) {
                setCurrentTranslatingLanguage(selectedLanguageNames[currentLangIndex]);
              }
              
              return newProgress;
            }
            return prev;
          });
        }, 800);

        const response = await axios.post('/translate/sms', requestData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        clearInterval(progressInterval);
        setTranslationProgress(100);
        setCurrentTranslatingLanguage('Finalizing...');

        // Wait a moment to show 100%
        await new Promise(resolve => setTimeout(resolve, 500));

        if (response.status === 200) {
          navigate('/library/creatives');
        }
      }
    } catch (err) {
      console.error(err.response || err);
      setError(err.response?.data?.message || 'Error adding creative');
    } finally {
      hideLoading();
      setIsSubmitting(false);
      setTranslationProgress(0);
      setCurrentTranslatingLanguage('');
    }
  };

  const validateAndSubmit = (e) => {
    e.preventDefault();

    if (!creativeData.creativeType) {
      setError('Please select a creative type.');
      return;
    }

    setError('');
    handleSubmit(e);
  };

  // Helper function for SMS stats calculation
  const calculateSMSStats = (text) => {
    if (!text) return { totalCount: 0, totalSMS: 0, gsm: 0, unicode: 0, nextPart: 160 };
    
    const textLength = text.length;
    const gsmChars = /^[A-Za-z0-9@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\[\]\|~€]*$/;
    const isUnicode = !gsmChars.test(text);
    
    let smsLimit = isUnicode ? 70 : 160;
    let totalSMS = Math.ceil(textLength / smsLimit);
    let nextPart = smsLimit - (textLength % smsLimit);
    
    if (nextPart === smsLimit) nextPart = 0;
    
    return {
      totalCount: textLength,
      totalSMS: totalSMS,
      gsm: isUnicode ? 0 : textLength,
      unicode: isUnicode ? textLength : 0,
      nextPart: nextPart
    };
  };

  // SMS Preview Component
  const renderSMSPreview = () => {
    // Get preview content based on mode and selected language
    let previewContent = '';
    let statsContent = '';

    if (creationMode === 'translate') {
      // In translate mode, show the original content
      statsContent = creativeData.body || '';
      previewContent = creativeData.body || 'SMS Body';
    } else {
      // In manual mode, show content for selected preview language
      if (previewLanguage && manualLanguageData[previewLanguage]?.body) {
        statsContent = manualLanguageData[previewLanguage].body;
        previewContent = statsContent;
      } else {
        statsContent = '';
        previewContent = 'Enter SMS content for selected language';
      }
    }

    return (
      <div className="mobile-preview-container">
        <div className="mobile-frame sms-frame">
          <div className="mobile-header">
            <div className="signal-bars"></div>
            <div className="time">12:34</div>
            <div className="battery">100%</div>
          </div>
          <div className="sms-conversation">
            <div className="sms-bubble received">
              <div className="sms-text">{previewContent}</div>
              <div className="sms-time">12:34</div>
            </div>
          </div>
        </div>
        <div className="sms-stats">
          {(() => {
            const stats = calculateSMSStats(statsContent);
            return (
              <div className="text-counter">
                <div className="counter-row">
                  <span className="counter-section">Total Count: {stats.totalCount}</span>
                </div>
                <div className="counter-row">
                  <span className="counter-section">Total SMS: {stats.totalSMS}</span>
                </div>
                <div className="counter-row">
                  <span className="counter-section">GSM Characters: {stats.gsm}</span>
                </div>
                <div className="counter-row total">
                  <span className="counter-section"><strong>Next Part: {stats.nextPart} characters remaining</strong></span>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  // Get available languages for preview
  const getPreviewLanguages = () => {
    // Return selected languages for both modes
    return selectAll 
      ? languages 
      : languages.filter(lang => selectedLanguages.includes(lang._id));
  };

  // Set initial preview language when languages are selected
  useEffect(() => {
    if (creationMode === 'manual') {
      const availableLangs = getPreviewLanguages();
      if (availableLangs.length > 0) {
        setPreviewLanguage(availableLangs[0]._id);
      }
    } else {
      setPreviewLanguage(null);
    }
  }, [selectedLanguages, selectAll, languages, creationMode]);

  return (
    <div className="add-creative-container">
      {/* Header Section */}
      <div className="header-section">
        <motion.button 
          className="back-button"
          onClick={() => navigate(-1)}
          whileHover={{ scale: 1.05 }}
        >
          <IoArrowBack /> Back
        </motion.button>
        <h1 className="page-title">{pageTitle}</h1>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Main Content - Two Column Layout */}
      <div className="form-with-preview">
        {/* Left Column - Form */}
        <motion.form 
          onSubmit={validateAndSubmit}
          className={`creative-form ${creationMode === 'manual' ? 'manual-mode' : ''}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
        {/* Title Input */}
        <div className="form-group">
          <label className="input-label">SMS Title</label>
          <input
            type="text"
            name="title"
            className="form-input"
            placeholder="Enter a title for your SMS"
            value={creativeData.title}
            onChange={handleChange}
            required
          />
        </div>

        {/* Tags Input */}
        <div className="form-group">
          <label className="input-label">Tags</label>
          <TagsInput
            value={creativeData.tags}
            onChange={(tags) => setCreativeData({ ...creativeData, tags })}
            placeholder="Type a tag and press Enter"
          />
        </div>

        {/* AI Translation / Manual Input Toggle */}
        {canAITranslateSMS && (
          <div className="form-group ai-mode-toggle">
            <label className="input-label">Content Mode</label>
            <div className="ai-toggle-row">
              <span className="ai-toggle-label">
                {creationMode === 'translate' ? 'AI Translation' : 'Manual Input'}
              </span>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={creationMode === 'translate'}
                  onChange={(e) => setCreationMode(e.target.checked ? 'translate' : 'manual')}
                />
                <span className="slider"></span>
              </label>
            </div>
          </div>
        )}

        {/* Creative Type Selector */}
        <div className="form-group">
          <label className="input-label">Creative Type</label>
          <select
            className="form-select"
            value={creativeData.creativeType}
            onChange={(e) => setCreativeData({ ...creativeData, creativeType: e.target.value })}
            required
          >
            <option value="">Select Creative Type</option>
            {Object.entries(creativeTypes).map(([key, value]) => (
              <option key={key} value={key}>{value}</option>
            ))}
          </select>
        </div>

        {/* Language Selection */}
        <div className="form-group">
          <label className="input-label">Languages</label>
          <div className="language-selector">
            <motion.div
              className={`language-option ${selectAll ? 'selected' : ''}`}
              onClick={toggleSelectAll}
              whileHover={{ scale: 1.02 }}
            >
              All Languages
            </motion.div>
            {languages.map((language) => (
              <motion.div
                key={language._id}
                className={`language-option ${(selectAll || selectedLanguages.includes(language._id)) ? 'selected' : ''}`}
                onClick={() => toggleLanguageSelection(language._id)}
                whileHover={{ scale: 1.02 }}
              >
                {language.code?.toUpperCase() || language.languageName}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Status Selection - Show only if system is enabled */}
        {statusSystemEnabled && (
          <div className="form-group">
            <label className="input-label">Status <span className="required-asterisk">*</span></label>
            <select
              className="form-select"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              required
            >
              <option value="">Select Status</option>
              {statuses.map((status) => (
                <option key={status._id} value={status._id}>
                  {status.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Conditional Content Based on Creation Mode */}
        {creationMode === 'translate' ? (
          <>
            {/* AI Translate Mode - Original Content */}
            {/* Content Editor */}
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label className="input-label">Content</label>
                <PlaceholderDropdown />
              </div>
              <textarea
                name="body"
                className="form-textarea"
                placeholder="Enter your creative content here..."
                value={creativeData.body || ''}
                onChange={handleChange}
                rows={6}
                required
              />
              {(() => {
                const bodyText = creativeData.body || '';
                const currentStats = calculateSMSStats(bodyText);
                return (
                  <div className="sms-stats" key={`stats-${bodyText.length}-${currentStats.nextPart}`}>
                    <span>Total Count: {currentStats.totalCount}</span>
                    <span>Total SMS: {currentStats.totalSMS}</span>
                    <span>GSM Characters: {currentStats.gsm}</span>
                    <span>Next Part: {currentStats.nextPart} characters remaining</span>
                  </div>
                );
              })()}
            </div>
          </>
        ) : (
          <>
            {/* Manual Input Mode */}
            <div className="manual-languages-container">
              {(selectAll ? languages : languages.filter(lang => selectedLanguages.includes(lang._id))).map((language) => (
                <div 
                  key={language._id} 
                  className={`manual-language-section ${emptyLanguages.includes(language._id) ? 'error' : ''}`}
                >
                  <h3 className="language-section-title">{language.code?.toUpperCase() || language.languageName}</h3>

                {/* Manual Body */}
                <div className="form-group">
                  <label className="input-label">SMS Content ({language.code?.toUpperCase() || language.languageName})</label>
                  <textarea
                    className="form-textarea"
                    placeholder={`Enter SMS content in ${language.code?.toUpperCase() || language.languageName}`}
                    value={manualLanguageData[language._id]?.body || ''}
                    onChange={(e) => handleManualLanguageChange(language._id, 'body', e.target.value)}
                    rows={6}
                    required
                  />
                  {(() => {
                    const langBody = manualLanguageData[language._id]?.body || '';
                    const currentStats = calculateSMSStats(langBody);
                    return (
                      <div className="sms-stats" key={`stats-${language._id}-${langBody.length}-${currentStats.nextPart}`}>
                        <span>Total Count: {currentStats.totalCount}</span>
                        <span>Total SMS: {currentStats.totalSMS}</span>
                        <span>GSM Characters: {currentStats.gsm}</span>
                        <span>Next Part: {currentStats.nextPart} characters remaining</span>
                      </div>
                    );
                  })()}
                </div>
                </div>
              ))}
            </div>
          </>
        )}        {/* Submit Button */}
        <div className="form-actions">
          <motion.button 
            type="submit" 
            className="submit-button"
            disabled={isSubmitting}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            {isSubmitting ? (
              <>
                <span className="button-spinner"></span>
                Creating...
              </>
            ) : (
              'Create SMS'
            )}
          </motion.button>
        </div>
      </motion.form>

      {/* Right Column - Preview Panel */}
      <div className="preview-panel">
        <div className="preview-header">
          <h3>SMS Preview</h3>
          
          {/* Language Tabs - Only in Manual Mode */}
          {creationMode === 'manual' && getPreviewLanguages().length > 0 && (
            <div className="preview-language-tabs">
              {getPreviewLanguages().map((lang) => (
                <button
                  key={lang._id}
                  className={`language-tab ${previewLanguage === lang._id ? 'active' : ''}`}
                  onClick={() => setPreviewLanguage(lang._id)}
                >
                  {lang.code?.toUpperCase() || lang.languageName}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="preview-container">
          {renderSMSPreview()}
        </div>
      </div>
    </div>

      {/* Enhanced Translation Loading Overlay */}
      {isSubmitting && (
        <div className="translation-loading-overlay">
          <div className="translation-loading-content">
            <div className="translation-header">
              <h2>Creating SMS Creative</h2>
              <p>Please wait while we create your SMS in the selected languages</p>
            </div>
            
            <div className="translation-progress">
              <div className="progress-circle">
                <svg className="progress-ring" width="120" height="120">
                  <circle
                    className="progress-ring-circle-bg"
                    stroke="#e5e7eb"
                    strokeWidth="8"
                    fill="transparent"
                    r="52"
                    cx="60"
                    cy="60"
                  />
                  <circle
                    className="progress-ring-circle"
                    stroke="#4a90e2"
                    strokeWidth="8"
                    fill="transparent"
                    r="52"
                    cx="60"
                    cy="60"
                    style={{
                      strokeDasharray: `${2 * Math.PI * 52}`,
                      strokeDashoffset: `${2 * Math.PI * 52 * (1 - translationProgress / 100)}`,
                      transform: 'rotate(-90deg)',
                      transformOrigin: '50% 50%'
                    }}
                  />
                </svg>
                <div className="progress-text">
                  <span className="progress-percentage">{Math.round(translationProgress)}%</span>
                </div>
              </div>
              
              <div className="translation-info">
                <div className="translation-stats">
                  <div className="stat">
                    <span className="stat-label">Languages to create:</span>
                    <span className="stat-value">{totalLanguages}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Progress:</span>
                    <span className="stat-value">{Math.round(translationProgress)}%</span>
                  </div>
                </div>
                
                {currentTranslatingLanguage && (
                  <div className="current-language">
                    <span className="current-language-label">Currently processing:</span>
                    <span className="current-language-name">{currentTranslatingLanguage}</span>
                  </div>
                )}
                
                <div className="progress-bar">
                  <div className="progress-bar-bg">
                    <div 
                      className="progress-bar-fill"
                      style={{ width: `${translationProgress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="translation-footer">
              <p className="translation-note">
                This process may take a few moments depending on the amount of content and number of languages.
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AddSMS;